import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { navForRole } from "@/components/roleNav";
import { useAuth } from "@/context/AuthContext";
import { managerService } from "@/services/paymentService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { LocalPager, useLocalPage } from "@/components/LocalPager";
import { toast } from "sonner";

const card = "rounded-md border border-[#22252F] bg-[#12141A] p-4";
const ROLE_LABEL = { manager: "Gestor", agent: "Agente Plus" };

export default function TeamMemberPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const clients = useLocalPage(data?.clients || [], 10);

  const load = useCallback(() => {
    managerService.teamMember(id).then(setData).catch((e) => toast.error(errorMessage(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const metrics = [
    { id: "clients", label: "Clientes", value: data?.clients_count ?? 0 },
    { id: "deposits-count", label: "Depósitos aprovados", value: data?.deposits_count ?? 0 },
    { id: "deposits-total", label: "Valor depositado", value: fmtMoney(data?.deposits_total || 0) },
    { id: "deposits-pending", label: "Depósitos pendentes", value: `${data?.deposits_pending_count ?? 0} · ${fmtMoney(data?.deposits_pending || 0)}` },
    { id: "withdrawals-total", label: "Retiradas aprovadas", value: `${data?.withdrawals_count ?? 0} · ${fmtMoney(data?.withdrawals_total || 0)}` },
    { id: "clients-balance", label: "Saldo dos clientes", value: fmtMoney(data?.clients_balance || 0) },
    { id: "pnl", label: "P/L realizado", value: fmtMoney(data?.realized_pnl || 0) },
    { id: "trades", label: "Operações", value: `${data?.trades ?? 0} (${data?.open_positions ?? 0} abertas)` },
  ];

  return (
    <AppShell
      nav={navForRole(user?.role)}
      title={data?.name || "Membro da equipa"}
      subtitle={`${ROLE_LABEL[data?.role] || ""} · ${data?.email || data?.phone_full || ""}`}
    >
      <div data-testid="team-member-page" className="space-y-6">
        <Link to={user?.role === "admin" ? "/admin/team" : "/manager"} data-testid="back-to-team"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </Link>

        <div className={`${card} flex flex-wrap gap-6 text-xs text-[#94A3B8]`} data-testid="team-member-meta">
          <span>Estado: <span className={data?.status === "active" ? "text-[#10B981]" : "text-[#EF4444]"}>{data?.status}</span></span>
          {data?.manager_name ? <span>Gestor: <span className="text-white">{data.manager_name}</span></span> : null}
          <span>Último acesso: <span className="font-mono text-white">{fmtDate(data?.last_login_at)}</span></span>
          <span>IP: <span className="font-mono text-white">{data?.last_ip || "—"}</span></span>
          <span>Documentos pendentes: <span className="text-white">{data?.documents_pending ?? 0}</span></span>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.id} data-testid={`team-metric-${m.id}`} className={card}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{m.label}</p>
              <p className="mt-2 font-mono text-sm text-white">{m.value}</p>
            </div>
          ))}
        </div>

        {data?.role === "manager" ? (
          <section className={card} data-testid="team-member-agents">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#60A5FA]">Agentes Plus</p>
            <div className="mt-3 space-y-2">
              {(data.agents || []).map((a) => (
                <Link key={a.id} to={`/team/${a.id}`} data-testid={`agent-link-${a.id}`}
                      className="flex justify-between border-b border-[#1A1D25] py-2 text-sm text-[#C7D0DE] hover:text-white">
                  <span>{a.name}</span>
                  <span className="font-mono text-xs text-[#94A3B8]">{a.email}</span>
                </Link>
              ))}
              {(data.agents || []).length === 0 ? (
                <p className="py-4 text-sm text-[#64748B]">Sem agentes atribuídos</p>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className={card} data-testid="team-member-clients">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#60A5FA]">Clientes</p>
          <div className="mt-3 space-y-2">
            {clients.slice.map((c) => (
              <Link key={c.id} to={`/clients/${c.id}`} data-testid={`team-client-${c.id}`}
                    className="flex flex-wrap justify-between gap-2 border-b border-[#1A1D25] py-2 text-sm text-[#C7D0DE] hover:text-white">
                <span>{c.name}</span>
                <span className="font-mono text-xs text-[#94A3B8]">{c.email}</span>
                <span className="font-mono text-xs text-white">{fmtMoney(c.balance)}</span>
              </Link>
            ))}
            {clients.total === 0 ? <p className="py-4 text-sm text-[#64748B]">Sem clientes</p> : (
              <LocalPager page={clients.page} pages={clients.pages} total={clients.total}
                          onPage={clients.setPage} testId="team-clients-pager" />
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
